import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, generateStrongPassword } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TopBar from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, MapPin, Users2, ArrowRight, Loader2, Building2, Lock, Unlock, KeyRound, RefreshCw } from "lucide-react";
export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    client_name: "", address: "", city: "", type: "", size: "", year: "", notes: "", password: "",
  });
  const [unlockTarget, setUnlockTarget] = useState(null);
  const [unlockPwd, setUnlockPwd] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  const load = () => {
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => setProjects([]));
  };
  useEffect(load, []);

  const openProject = (p) => {
    if (isAdmin || !p.locked) {
      navigate(`/projects/${p.id}`);
    } else {
      setUnlockPwd("");
      setUnlockTarget(p);
    }
  };

  const doUnlock = async () => {
    if (!unlockPwd) { toast.error("Inserisci la password del progetto"); return; }
    setUnlocking(true);
    try {
      await api.post(`/projects/${unlockTarget.id}/unlock`, { password: unlockPwd });
      toast.success("Progetto sbloccato");
      const id = unlockTarget.id;
      setUnlockTarget(null);
      navigate(`/projects/${id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUnlocking(false);
    }
  };

  const create = async () => {
    if (!form.client_name.trim()) {
      toast.error("Inserisci nome e cognome del cliente");
      return;
    }
    if (!form.password.trim()) {
      toast.error("Imposta una password per il progetto");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/projects", {
        client_name: form.client_name,
        password: form.password,
        property_data: {
          address: form.address, city: form.city, type: form.type,
          size: form.size, year: form.year, notes: form.notes,
        },
      });
      toast.success("Progetto creato");
      setOpen(false);
      setForm({ client_name: "", address: "", city: "", type: "", size: "", year: "", notes: "", password: "" });
      navigate(`/projects/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain">
      <TopBar />
      <main className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <div className="fade-up">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground">
              Tutti i progetti
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight mt-3">
              Ciao, {user?.name?.split(" ")[0] || "benvenuto"}.
            </h1>
            <p className="text-sm text-muted-foreground mt-3 max-w-lg">
              {isAdmin
                ? "Ogni progetto è una cartella protetta da password: la imposti tu e la condividi con chi lavora al progetto."
                : "Ogni progetto è protetto da una password. Apri quelli per cui lo studio Ordine3 ti ha fornito la password."}
            </p>
          </div>

          {isAdmin && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="create-project-button"
                  className="rounded-none h-11 gap-2 bg-foreground text-background hover:bg-foreground/90"
                >
                  <Plus className="h-4 w-4" /> Nuovo progetto
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading font-light text-2xl">Nuovo progetto</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <Field label="Nome e cognome cliente" required>
                    <Input data-testid="project-client-input" value={form.client_name}
                      onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                      className="rounded-none focus-visible:ring-foreground" />
                  </Field>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Indirizzo immobile">
                      <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                        className="rounded-none focus-visible:ring-foreground" />
                    </Field>
                    <Field label="Città">
                      <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                        className="rounded-none focus-visible:ring-foreground" />
                    </Field>
                    <Field label="Tipologia">
                      <Input placeholder="Appartamento, villa..." value={form.type}
                        onChange={(e) => setForm({ ...form, type: e.target.value })}
                        className="rounded-none focus-visible:ring-foreground" />
                    </Field>
                    <Field label="Superficie (mq)">
                      <Input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })}
                        className="rounded-none focus-visible:ring-foreground" />
                    </Field>
                  </div>
                  <Field label="Note immobile">
                    <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="rounded-none focus-visible:ring-foreground" rows={3} />
                  </Field>
                  <Field label="Password del progetto" required>
                    <div className="flex gap-2">
                      <Input data-testid="project-password-input" value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        placeholder="Es. Cantiere-Rossi-2026"
                        className="rounded-none focus-visible:ring-foreground" />
                      <Button type="button" variant="outline" data-testid="generate-password-button"
                        onClick={() => setForm({ ...form, password: generateStrongPassword() })}
                        className="rounded-none gap-2 shrink-0">
                        <RefreshCw className="h-4 w-4" /> Genera
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">Condividi questa password solo con chi lavora a questo progetto.</p>
                  </Field>
                </div>
                <DialogFooter>
                  <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Annulla</Button>
                  <Button data-testid="project-save-button" onClick={create} disabled={saving}
                    className="rounded-none bg-foreground text-background hover:bg-foreground/90">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea progetto"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {projects === null ? (
          <div className="py-24 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : projects.length === 0 ? (
          <div className="border border-border border-dashed py-24 text-center fade-up">
            <Building2 className="h-10 w-10 mx-auto text-muted-foreground/50" strokeWidth={1} />
            <p className="mt-4 font-heading text-xl font-light">Nessun progetto</p>
            <p className="text-sm text-muted-foreground mt-1">
              {isAdmin ? "Crea il primo progetto per iniziare." : "Non ci sono ancora progetti."}
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
            {projects.map((p, i) => {
              const locked = !isAdmin && p.locked;
              return (
              <button
                key={p.id}
                data-testid={`project-card-${p.id}`}
                onClick={() => openProject(p)}
                style={{ animationDelay: `${i * 40}ms` }}
                className="group text-left border-r border-b border-border p-6 bg-white hover:bg-secondary transition-colors fade-up relative"
              >
                <div className="flex items-start justify-between">
                  <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-muted-foreground">
                    {p.property_data?.type || "Progetto"}
                  </span>
                  {locked ? (
                    <Lock className="h-4 w-4 text-foreground" data-testid={`lock-${p.id}`} />
                  ) : (
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                  )}
                </div>
                <h3 className="font-heading text-2xl font-light mt-6 tracking-tight">{p.client_name}</h3>
                <div className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                  <p className="flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5" />
                    {[p.property_data?.address, p.property_data?.city].filter(Boolean).join(", ") || "Immobile da definire"}
                  </p>
                  {isAdmin ? (
                    <p className="flex items-center gap-2" data-testid={`card-password-${p.id}`}>
                      <KeyRound className="h-3.5 w-3.5" />
                      Password: <span className="font-mono text-foreground">{p.password || "—"}</span>
                    </p>
                  ) : (
                    <p className="flex items-center gap-2">
                      {locked ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}
                      {locked ? "Protetto da password" : "Sbloccato"}
                    </p>
                  )}
                </div>
                <div className="mt-5">
                  <div className="flex items-center justify-between text-[11px] mb-1.5">
                    <span className="uppercase tracking-[0.12em] text-muted-foreground">{p.status || "Progettazione"}</span>
                    <span className="font-medium text-foreground">{p.progress ?? 0}%</span>
                  </div>
                  <div className="h-1 w-full bg-secondary">
                    <div className="h-full bg-foreground transition-[width] duration-500" style={{ width: `${p.progress ?? 0}%` }} />
                  </div>
                </div>
              </button>
              );
            })}
          </div>
        )}
      </main>

      <Dialog open={!!unlockTarget} onOpenChange={(o) => !o && setUnlockTarget(null)}>
        <DialogContent className="rounded-none sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-light text-2xl flex items-center gap-2">
              <Lock className="h-5 w-5" /> Progetto protetto
            </DialogTitle>
            <DialogDescription>
              Inserisci la password del progetto <strong>{unlockTarget?.client_name}</strong> fornita dallo studio Ordine3.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Input
              data-testid="unlock-password-input"
              type="password"
              autoFocus
              value={unlockPwd}
              onChange={(e) => setUnlockPwd(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doUnlock()}
              placeholder="Password del progetto"
              className="rounded-none focus-visible:ring-foreground h-11"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setUnlockTarget(null)}>Annulla</Button>
            <Button data-testid="unlock-submit-button" onClick={doUnlock} disabled={unlocking}
              className="rounded-none bg-foreground text-background hover:bg-foreground/90 gap-2">
              {unlocking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlock className="h-4 w-4" />} Sblocca
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold tracking-[0.08em] uppercase text-muted-foreground">
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      {children}
    </div>
  );
}
